import React, { useState, useEffect, useCallback } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';
import { API_ENDPOINTS } from '../../config/api';

interface Wallet {
  id: string;
  name: string;
  balance: number;
  type: 'personal' | 'chama' | 'business' | 'savings';
  currency: string;
}

interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'transfer' | 'payment';
  amount: number;
  description: string;
  created_at: string;
}

const Wallet: React.FC = () => {
  const colors = getThemeColors('dark');

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [realTimeBalance, setRealTimeBalance] = useState<number | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<Wallet | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);

  // Mock data - in real app this would come from API/context
  const mockWallets: Wallet[] = [
    {
      id: '1',
      name: 'Personal Wallet',
      balance: 25000,
      type: 'personal',
      currency: 'KES'
    }
  ];

  const mockTransactions: Transaction[] = [
    {
      id: '1',
      type: 'deposit',
      amount: 5000,
      description: 'M-Pesa Deposit',
      created_at: '2024-01-15T10:30:00Z'
    },
    {
      id: '2',
      type: 'withdrawal',
      amount: -2000,
      description: 'ATM Withdrawal',
      created_at: '2024-01-14T14:20:00Z'
    },
    {
      id: '3',
      type: 'transfer',
      amount: 1000,
      description: 'Chama Contribution',
      created_at: '2024-01-13T09:15:00Z'
    }
  ];

  // Load real-time balance from API
  const loadRealTimeBalance = useCallback(async () => {
    try {
      setBalanceLoading(true);
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.error('No auth token found');
        setRealTimeBalance(0);
        return;
      }

      const response = await fetch(API_ENDPOINTS.WALLET_BALANCE, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setRealTimeBalance(data.data.balance || 0);
      } else {
        console.error('Failed to load wallet balance:', data.error);
        setRealTimeBalance(0);
      }
    } catch (error) {
      console.error('Error loading wallet balance:', error);
      setRealTimeBalance(0);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  // Effect for setting selected wallet when wallets change
  useEffect(() => {
    if (mockWallets.length > 0) {
      setSelectedWallet(mockWallets.find(w => w.type === 'personal') || mockWallets[0]);
    }
  }, []);

  
  // Effect for loading balance and setting up auto-refresh
  useEffect(() => {
    loadRealTimeBalance();

    // Set up auto-refresh for balance every 30 seconds
    const balanceInterval = setInterval(() => {
      loadRealTimeBalance();
    }, 30000);

    return () => {
      clearInterval(balanceInterval);
    };
  }, [loadRealTimeBalance]);

  const onRefresh = async () => {
    setRefreshing(true);
    // Mock refresh - in real app this would reload data
    await new Promise(resolve => setTimeout(resolve, 1000));
    await loadRealTimeBalance();
    setRefreshing(false);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getTotalBalance = (): number => {
    if (realTimeBalance !== null && realTimeBalance !== undefined) {
      return realTimeBalance;
    }
    return mockWallets.reduce((sum, wallet) => sum + wallet.balance, 0);
  };

  const getWalletIcon = (type: string): string => {
    switch (type) {
      case 'personal': return 'wallet';
      case 'chama': return 'people';
      case 'business': return 'briefcase';
      case 'savings': return 'piggy-bank';
      default: return 'card';
    }
  };

  const getWalletColor = (type: string): string => {
    switch (type) {
      case 'personal': return colors.primary;
      case 'chama': return colors.secondary;
      case 'business': return colors.info;
      case 'savings': return colors.success;
      default: return colors.textSecondary;
    }
  };

  const getTransactionIcon = (type: string): string => {
    switch (type) {
      case 'deposit': return 'arrow-down';
      case 'withdrawal': return 'arrow-up';
      case 'transfer': return 'swap-horizontal';
      case 'payment': return 'card';
      default: return 'cash';
    }
  };

  const getTransactionColor = (type: string): string => {
    switch (type) {
      case 'deposit': return colors.success;
      case 'withdrawal': return colors.error;
      case 'transfer': return colors.info;
      case 'payment': return colors.warning;
      default: return colors.textSecondary;
    }
  };

  const handleAction = (action: string) => {
    console.log(`${action} action triggered`);
    // In real app, this would navigate to the respective screen
    // For now, we'll use window.location for navigation
    switch (action) {
      case 'transfer':
        window.location.href = '/dashboard/wallet/transfer';
        break;
      case 'request':
        window.location.href = '/dashboard/wallet/request';
        break;
      case 'deposit':
        window.location.href = '/dashboard/wallet/deposit';
        break;
      case 'withdraw':
        window.location.href = '/dashboard/wallet/withdraw';
        break;
      case 'history':
        window.location.href = '/dashboard/transactions';
        break;
      case 'ai':
        window.location.href = '/dashboard/ai-assistant';
        break;
      default:
        console.log(`Unknown action: ${action}`);
    }
  };

  const renderWalletHeader = () => (
    <div style={{
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
    }}>
      <div style={{
        alignItems: 'center',
        marginBottom: spacing.xl,
      }}>
        <div style={{
          fontSize: typography.fontSize.base,
          color: colors.textSecondary,
          marginBottom: spacing.sm,
        }}>
          Total Balance
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: spacing.md,
        }}>
          {balanceLoading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
            }}>
              <div style={{
                width: 16,
                height: 16,
                border: `2px solid ${colors.primary}`,
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }} />
              <span style={{
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                fontStyle: 'italic',
              }}>
                Updating...
              </span>
            </div>
          ) : (
            <span style={{
              fontSize: typography.fontSize['3xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.text,
            }}>
              {formatCurrency(getTotalBalance())}
            </span>
          )}
          <button
            onClick={loadRealTimeBalance}
            style={{
              padding: spacing.sm,
              borderRadius: borderRadius.full,
              backgroundColor: colors.primary + '20',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <span style={{ color: colors.primary, fontSize: 16 }}>🔄</span>
          </button>
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: spacing.sm,
        flexWrap: 'wrap',
      }}>
        <button
          style={{
            flex: 1,
            minWidth: 80,
            padding: spacing.md,
            borderRadius: borderRadius.lg,
            backgroundColor: colors.primary + '20',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: spacing.sm,
          }}
          onClick={() => handleAction('transfer')}
        >
          <span style={{ fontSize: 24, color: colors.primary }}>📤</span>
          <span style={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: colors.primary,
          }}>
            Send
          </span>
        </button>

        <button
          style={{
            flex: 1,
            minWidth: 80,
            padding: spacing.md,
            borderRadius: borderRadius.lg,
            backgroundColor: colors.success + '20',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: spacing.sm,
          }}
          onClick={() => handleAction('request')}
        >
          <span style={{ fontSize: 24, color: colors.success }}>📥</span>
          <span style={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: colors.success,
          }}>
            Receive
          </span>
        </button>

        <button
          style={{
            flex: 1,
            minWidth: 80,
            padding: spacing.md,
            borderRadius: borderRadius.lg,
            backgroundColor: colors.info + '20',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: spacing.sm,
          }}
          onClick={() => handleAction('deposit')}
        >
          <span style={{ fontSize: 24, color: colors.info }}>➕</span>
          <span style={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: colors.info,
          }}>
            Top Up
          </span>
        </button>

        <button
          style={{
            flex: 1,
            minWidth: 80,
            padding: spacing.md,
            borderRadius: borderRadius.lg,
            backgroundColor: colors.error + '20',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: spacing.sm,
          }}
          onClick={() => handleAction('withdraw')}
        >
          <span style={{ fontSize: 24, color: colors.error }}>➖</span>
          <span style={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: colors.error,
          }}>
            Withdraw
          </span>
        </button>
      </div>
    </div>
  );

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    header: {
      textAlign: 'center',
      marginBottom: spacing.xl,
    },
    title: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    subtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    quickActions: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
    },
    sectionTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.md,
    },
    quickActionsGrid: {
      display: 'flex',
      gap: spacing.md,
      flexWrap: 'wrap',
    },
    quickActionCard: {
      flex: 1,
      minWidth: 120,
      padding: spacing.lg,
      borderRadius: borderRadius.lg,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
    },
    quickActionText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      marginTop: spacing.sm,
    },
    recentTransactions: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      ...getShadowStyle('md'),
    },
    transactionItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: `${spacing.md}px 0`,
      borderBottom: `1px solid ${colors.border}`,
    },
    transactionInfo: {
      flex: 1,
    },
    transactionType: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.text,
      marginBottom: spacing.xs / 2,
    },
    transactionDate: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
    },
    transactionAmount: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.success,
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Wallet"
        showBackButton={false}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {renderWalletHeader()}

          <div style={styles.quickActions}>
            <h2 style={styles.sectionTitle}>Quick Actions</h2>
            <div style={styles.quickActionsGrid}>
              <button
                style={{
                  ...styles.quickActionCard,
                  backgroundColor: colors.primary + '20',
                }}
                onClick={() => handleAction('history')}
              >
                <span style={{ fontSize: 24, color: colors.primary }}>📋</span>
                <span style={{
                  ...styles.quickActionText,
                  color: colors.primary,
                }}>
                  History
                </span>
              </button>

              <button
                style={{
                  ...styles.quickActionCard,
                  backgroundColor: colors.success + '20',
                }}
                onClick={() => handleAction('ai')}
              >
                <span style={{ fontSize: 24, color: colors.success }}>✨</span>
                <span style={{
                  ...styles.quickActionText,
                  color: colors.success,
                }}>
                  AI Advisor
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wallet;